/* eslint react-hooks/exhaustive-deps: "warn" */
import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MsvDelivery.module.scss';

type Translate = (key: string) => string;

const CODE = `POST /v2/verify/trader

{
  "country": "SE",
  "registration_number": "556789-1234",
  "declared_name": "NORDVIK TRADING AB",
  "declared_address": "Kungsgatan 12, Stockholm",
  "screen": ["entity", "officers", "ubo"]
}

→ 200
{
  "verdict": "verified",
  "name":    { "state": "match" },
  "address": { "state": "differs" },
  "status":  "active",
  "graph":   { "suspended_links": 0 },
  "source":  "bolagsverket",
  "synced_at": "2026-07-26"
}`;

// Proper-noun / technical pill labels (MCP server, Regis AI, Salesforce, HubSpot)
// are left unwrapped.
const getPills = (t: Translate) => [
  { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
  { label: t('Add to ChatGPT'), href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
  { label: 'MCP server', href: '/mcp-integration' },
  { label: 'Regis AI', href: '/regis' },
  { label: 'Salesforce', href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
  { label: 'HubSpot', href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
  { label: t('Browser extension'), href: '/browser-extension' },
  { label: t('Batch file'), href: '/contact-us' },
  { label: t('API docs'), href: 'https://api.globaldatabase.com/docs/v2' },
];

const getDeliveryItems = (t: Translate) => [
  {
    key: 'API',
    title: t('Verify inside the signup flow'),
    body: t(
      'One request returns the verdict, the field comparison and the source. Sellers who resolve cleanly never reach a review queue.',
    ),
  },
  {
    key: 'Batch',
    title: t('Check the sellers you already have'),
    body: t(
      'Run the existing base through the same check. Dissolved entities and mismatched identifiers surface in the first pass.',
    ),
  },
  {
    key: 'Feed',
    title: t('Hear about it when something changes'),
    body: t(
      'Status, name, address, officer and ownership movements on every verified seller, pushed daily to a webhook or bucket.',
    ),
  },
  {
    key: 'MCP',
    title: t('Ask in plain language'),
    body: t(
      'Trust and safety gets the registry record with the source attached, without opening a portal or filing a data request.',
    ),
  },
  {
    key: 'Rights',
    title: t('Show verified identity to buyers'),
    body: t(
      'Licensing permits publishing seller identity on your listings, which the INFORM Act and the GPSR both require.',
    ),
  },
];

export const MsvDelivery = () => {
  const { t } = useInt();

  const pills = getPills(t);
  const deliveryItems = getDeliveryItems(t);

  return (
    <Section background="#f6f7f9" className={styles.root}>
      <TitleBlock title={t('One call, inside the flow you already have.')} align="left" />

      <div className={styles.grid}>
        <div>
          <div className={styles.codeBlock}>
            <pre className={styles.code}>{CODE}</pre>
          </div>
          <span className={styles.pillLabel}>{t('Run verification from')}</span>
          <div className={styles.pills}>
            {pills.map((p) => (
              <Button key={p.label} href={p.href} className={styles.pill} type="text" size="sm">
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        <div className={styles.dlist}>
          {deliveryItems.map((item) => (
            <div key={item.key} className={styles.ditem}>
              <span className={styles.dkey}>{item.key}</span>
              <div>
                <h3 className={styles.dtitle}>{item.title}</h3>
                <p className={styles.dbody}>{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
